/* eslint-disable no-param-reassign */
import { createContext } from 'react';
import { cloneDeep, get } from 'lodash';

const VALUE_MARKER = '@value';
const COLLECTION_MARKER = '[*]';

/**
 * Index des itérations (champs itérables / collections) qui entourent un champ,
 * du plus externe au plus interne. Fourni par le template de tableau
 * (NeoTemplate/Array) : RJSF ne transmet pas l'index aux sous-champs, et
 * `idSchema.$id` n'est pas fiable (tronqué dans NeoSimpleTabs / NeoStepper).
 * Ex : [1] pour le 2ᵉ élément de "demarches", [1, 0] pour une collection imbriquée.
 */
export const IterationContext = createContext([]);

/**
 * Remplace chaque "[*]" d'un chemin par l'index d'itération correspondant.
 * Les "[*]" du chemin correspondent aux collections les plus externes : ils sont
 * remplis dans l'ordre avec les premiers index.
 * Ex : fillIndexes("demarches[*].theme", [1]) → "demarches[1].theme"
 *
 * @param {string} path - Chemin pouvant contenir des "[*]"
 * @param {array} indexes - Index des itérations, du plus externe au plus interne
 * @returns {string} Le chemin avec les index connus (les "[*]" restants sont conservés)
 */
export const fillIndexes = (path, indexes = []) => {
  if (typeof path !== 'string') {
    return path;
  }
  let position = 0;
  return path.replace(/\[\*\]/g, (marker) => {
    const index = indexes[position];
    position += 1;
    return index === undefined ? marker : `[${index}]`;
  });
};

/**
 * Chemin concret d'un champ dans le formData, à partir de son "@source" et des
 * index d'itération qui l'entourent. Si le contexte contient plus d'index que le
 * "@source" n'a de collections, ce sont les plus internes qui sont utilisés.
 * Ex : getConcretePath("demarches[*].sousTheme", [1]) → "demarches[1].sousTheme"
 *
 * @param {string} source - Le "@source" du champ
 * @param {array} indexes - Index des itérations (IterationContext)
 * @returns {string|undefined}
 */
export const getConcretePath = (source, indexes = []) => {
  if (typeof source !== 'string') {
    return source;
  }
  const markers = source.split(COLLECTION_MARKER).length - 1;
  if (markers === 0) {
    return source;
  }
  return fillIndexes(source, indexes.slice(-markers));
};

/**
 * Liste les chemins concrets existant dans le formData pour un "@source" : un
 * par itération présente.
 * Ex : "demarches[*].sousTheme" avec 2 démarches →
 *   [{ path: "demarches[0].sousTheme", indexes: [0] },
 *    { path: "demarches[1].sousTheme", indexes: [1] }]
 *
 * @param {string} source - Le "@source" du champ
 * @param {object} formdata - Les valeurs actuelles du formulaire
 * @returns {array} Liste de { path, indexes }
 */
export const expandSourcePath = (source, formdata) => {
  if (typeof source !== 'string' || !source.includes(COLLECTION_MARKER)) {
    return [{ path: source, indexes: [] }];
  }

  const expand = (parts, prefix, indexes) => {
    const [head, ...rest] = parts;
    const current = `${prefix}${head}`;
    if (rest.length === 0) {
      return [{ path: current, indexes }];
    }
    const items = get(formdata, current);
    if (!Array.isArray(items)) {
      return [];
    }
    return items.flatMap((_item, index) =>
      expand(rest, `${current}[${index}]`, [...indexes, index])
    );
  };

  return expand(source.split(COLLECTION_MARKER), '', []);
};

/**
 * Chemin du champ source référencé par la valeur d'un paramètre, ou null si la
 * valeur ne référence pas de champ. Comme historiquement, "@value" peut être
 * placé avant ou après le chemin : "@valueetapes.onglet_un.theme" et
 * "etapes.onglet_un.theme@value" désignent tous deux "etapes.onglet_un.theme".
 * "@value" seul désigne la valeur du champ lui-même (géré par triggerApi).
 *
 * @param {*} value - La valeur du paramètre
 * @returns {string|null}
 */
const getValueParamPath = (value) => {
  if (
    typeof value !== 'string' ||
    value === VALUE_MARKER ||
    !value.includes(VALUE_MARKER)
  ) {
    return null;
  }
  return value.replace(VALUE_MARKER, '');
};

/**
 * Remplace les paramètres référençant un champ ("@value<chemin>" ou
 * "<chemin>@value") par la valeur réelle de ce champ dans le formData. Les "[*]"
 * du chemin sont remplacés par les index de l'itération courante
 * (ex : "demarches[*].theme@value" → formdata.demarches[1].theme).
 * Si le champ source n'a pas de valeur, le paramètre garde sa valeur d'origine et
 * sera considéré comme manquant par triggerApi s'il est requis (pas d'appel API).
 * "@value" seul (valeur du champ lui-même) et "@filter" sont laissés à triggerApi.
 *
 * @param {array} params - Les paramètres d'une config "@dynamicData" / "@verificatorData"
 * @param {object} formdata - Les valeurs actuelles du formulaire
 * @param {array} indexes - Index des itérations du champ
 * @returns {array} Une copie des paramètres avec les valeurs résolues
 */
export const resolveValueParams = (params, formdata, indexes = []) => {
  if (!params || params.length === 0) {
    return [];
  }
  return cloneDeep(params).map((param) => {
    const valuePath = getValueParamPath(param?.value);
    if (valuePath !== null) {
      // Chemin du champ source avec les index de l'itération courante
      const sourcePath = fillIndexes(valuePath, indexes);
      // Récupère la valeur actuelle de ce champ dans le formdata
      const listeningData = get(formdata, sourcePath);
      if (listeningData !== undefined) {
        param.value = listeningData;
      }
    }
    return param;
  });
};

/**
 * Indique si des paramètres résolus contiennent encore une référence à un champ
 * ("@value<chemin>" ou "<chemin>@value") requise non remplacée (champ source
 * sans valeur).
 *
 * @param {array} params - Les paramètres résolus par resolveValueParams
 * @returns {boolean}
 */
export const hasUnresolvedParams = (params) =>
  params.some(
    (param) => param.required && getValueParamPath(param.value) !== null
  );

/**
 * Copie d'une config "@dynamicData" / "@verificatorData" avec ses paramètres
 * résolus, pour les appels faits directement par les composants (recherche de
 * l'autocomplete, vérification). Sans formData disponible, la config est
 * renvoyée telle quelle.
 *
 * @param {object} dataConfig - La config
 * @param {object} formdata - Les valeurs actuelles du formulaire
 * @param {array} indexes - Index des itérations du champ
 * @returns {object}
 */
export const withResolvedParams = (dataConfig, formdata, indexes = []) => {
  if (!dataConfig || !formdata) {
    return dataConfig;
  }
  return {
    ...dataConfig,
    params: resolveValueParams(dataConfig.params, formdata, indexes),
  };
};

/**
 * État de chargement d'un champ dynamique ("@fetched", "@paramsAtFetch",
 * "@isFetching", …). Hors collection, il est porté directement par la config
 * "@dynamicData" (comportement historique). Dans une collection, la config est
 * partagée par toutes les itérations : l'état est alors rangé par chemin concret
 * dans "@byPath" (ex : "@byPath"["demarches[1].sousTheme"]).
 *
 * @param {object} dynamicData - La config "@dynamicData" (mutée si create)
 * @param {string} path - Chemin concret du champ (getConcretePath)
 * @param {boolean} create - Crée l'état s'il n'existe pas encore
 * @returns {object} L'objet d'état (vide et non rattaché s'il n'existe pas)
 */
export const getDynamicDataState = (dynamicData, path, create = false) => {
  if (!dynamicData) {
    return {};
  }
  const source = dynamicData['@source'];
  if (
    !path ||
    typeof source !== 'string' ||
    !source.includes(COLLECTION_MARKER)
  ) {
    return dynamicData;
  }
  if (!dynamicData['@byPath']) {
    if (!create) {
      return {};
    }
    dynamicData['@byPath'] = {};
  }
  if (!dynamicData['@byPath'][path]) {
    if (!create) {
      return {};
    }
    dynamicData['@byPath'][path] = {};
  }
  return dynamicData['@byPath'][path];
};

/**
 * État de chargement (voir getDynamicDataState) du champ dont on a le schéma,
 * dans l'itération courante. À utiliser dans les composants de champ :
 *   const iterationIndexes = useContext(IterationContext);
 *   const dynamicState = getFieldDynamicDataState(schema, iterationIndexes);
 *
 * @param {object} schema - Le schéma du champ (contenant "@dynamicData")
 * @param {array} indexes - Index des itérations (IterationContext)
 * @returns {object} L'objet d'état ({} s'il n'existe pas encore)
 */
export const getFieldDynamicDataState = (schema, indexes = []) => {
  const dynamicData = schema?.['@dynamicData'];
  return getDynamicDataState(
    dynamicData,
    getConcretePath(dynamicData?.['@source'], indexes)
  );
};
